import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { financialAPI, timesheetsAPI, clientInvoicesAPI, auditAPI, usersAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { formatDate, formatDateRange, formatDateTime } from '../utils/dateUtils';
import {
  Clock,
  FileText,
  CreditCard,
  TrendingUp,
  Plus,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  User
} from 'lucide-react';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { className: 'badge-draft', label: 'Draft' },
    submitted: { className: 'badge-pending', label: 'Pending' },
    approved: { className: 'badge-approved', label: 'Approved' },
    rejected: { className: 'badge-rejected', label: 'Rejected' },
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <span className={config.className}>{config.label}</span>;
};

const KPICard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className="kpi-card" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <p className="mt-2 text-3xl font-bold text-slate-900">{value}</p>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [users, setUsers] = useState({});
  const [yetToBeCharged, setYetToBeCharged] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [kpisRes, pendingRes, activityRes, usersRes, yetToBeChargedRes] = await Promise.all([
        financialAPI.getKPIs(),
        timesheetsAPI.getPending(),
        auditAPI.getRecent(10),
        usersAPI.getAll(),
        financialAPI.getYetToBeCharged()
      ]);
      setKpis(kpisRes.data);
      setPendingTimesheets(pendingRes.data.slice(0, 5));
      setRecentActivity(activityRes.data);
      setYetToBeCharged(yetToBeChargedRes.data.total || 0);
      
      // Create a lookup map for users by ID
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u; });
      setUsers(usersMap);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        {/* Page header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="mt-1 text-slate-500">Overview of timesheets, invoices, and financial performance</p>
        </div>

        {/* KPI Cards - 3 cards only */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <KPICard
            title="Pending Approval"
            value={kpis?.pending_approval || 0}
            subtitle="Timesheets awaiting review"
            icon={Clock}
            color="bg-amber-500"
          />
          <KPICard
            title="Approved (Not Invoiced)"
            value={kpis?.approved_not_invoiced || 0}
            subtitle="Ready for invoicing"
            icon={FileText}
            color="bg-[#194eb2]"
          />
          <KPICard
            title="Yet to be Charged"
            value={`$${yetToBeCharged.toLocaleString()}`}
            subtitle="Approved, awaiting invoice"
            icon={CreditCard}
            color="bg-orange-500"
          />
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pending Timesheets */}
          <div className="lg:col-span-2 card-base">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Pending Approvals</h2>
              <Link to="/approvals" className="text-sm text-[#194eb2] hover:underline flex items-center gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="card-body">
              {pendingTimesheets.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-slate-600">All timesheets are reviewed!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingTimesheets.map((ts) => {
                    const submitter = users[ts.user_id];
                    return (
                      <Link
                        key={ts.id}
                        to={`/timesheets/${ts.id}`}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-slate-900 flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            {submitter?.name || 'Unknown User'}
                          </p>
                          <p className="text-sm text-slate-600 mt-1">
                            {formatDateRange(ts.period_start, ts.period_end)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {ts.total_days > 0 && `${ts.total_days} days`}
                            {ts.total_days > 0 && ts.total_hours > 0 && ', '}
                            {ts.total_hours > 0 && `${ts.total_hours} hours`}
                          </p>
                        </div>
                        <StatusBadge status={ts.status} />
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card-base">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-slate-900">Recent Activity</h2>
            </div>
            <div className="card-body">
              {recentActivity.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No recent activity</p>
              ) : (
                <div className="space-y-4">
                  {recentActivity.slice(0, 8).map((log) => (
                    <div key={log.id} className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-full ${
                        log.action === 'APPROVE' ? 'bg-emerald-100' :
                        log.action === 'REJECT' ? 'bg-red-100' :
                        log.action === 'SUBMIT' ? 'bg-amber-100' :
                        'bg-slate-100'
                      }`}>
                        {log.action === 'APPROVE' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
                         log.action === 'REJECT' ? <XCircle className="w-4 h-4 text-red-600" /> :
                         log.action === 'SUBMIT' ? <Clock className="w-4 h-4 text-amber-600" /> :
                         <FileText className="w-4 h-4 text-slate-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 truncate">
                          {log.action} {log.entity_type}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatDateTime(log.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const SubcontractorDashboard = () => {
  const { user } = useAuth();
  const [currentTimesheet, setCurrentTimesheet] = useState(null);
  const [myTimesheets, setMyTimesheets] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [periodRes, currentRes, allRes] = await Promise.all([
        timesheetsAPI.getCurrentPeriod(),
        timesheetsAPI.getMyCurrent(),
        timesheetsAPI.getMy()
      ]);
      setCurrentPeriod(periodRes.data);
      setCurrentTimesheet(currentRes.data);
      setMyTimesheets(allRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = {
    draft: myTimesheets.filter(t => t.status === 'draft').length,
    pending: myTimesheets.filter(t => t.status === 'submitted').length,
    approved: myTimesheets.filter(t => t.status === 'approved').length,
    rejected: myTimesheets.filter(t => t.status === 'rejected').length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        {/* Welcome header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Welcome, {user?.name}</h1>
          <p className="mt-1 text-slate-500">Manage your weekly timesheets</p>
        </div>

        {/* Current Week Card */}
        <div className="card-base">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate-900">Current Week</h2>
            <p className="text-sm text-slate-500">
              {formatDateRange(currentPeriod?.start_date, currentPeriod?.end_date)}
            </p>
          </div>
          <div className="card-body">
            {currentTimesheet ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">
                    {currentTimesheet.total_days > 0 && `${currentTimesheet.total_days} days`}
                    {currentTimesheet.total_days > 0 && currentTimesheet.total_hours > 0 && ', '}
                    {currentTimesheet.total_hours > 0 && `${currentTimesheet.total_hours} hours`}
                    {currentTimesheet.total_days === 0 && currentTimesheet.total_hours === 0 && 'No entries yet'}
                  </p>
                  <StatusBadge status={currentTimesheet.status} />
                </div>
                <Link
                  to={`/timesheets/${currentTimesheet.id}`}
                  className="btn-primary"
                  data-testid="edit-current-timesheet"
                >
                  {currentTimesheet.status === 'draft' ? 'Edit Timesheet' : 'View Timesheet'}
                </Link>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-slate-500">No timesheet for this week yet</p>
                <Link to="/timesheet/new" className="btn-primary flex items-center gap-2" data-testid="create-new-timesheet">
                  <Plus className="w-4 h-4" />
                  Create Timesheet
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="kpi-card">
            <p className="text-sm font-medium text-slate-500">Draft</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{stats.draft}</p>
          </div>
          <div className="kpi-card">
            <p className="text-sm font-medium text-slate-500">Pending</p>
            <p className="mt-1 text-2xl font-bold text-amber-600">{stats.pending}</p>
          </div>
          <div className="kpi-card">
            <p className="text-sm font-medium text-slate-500">Approved</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600">{stats.approved}</p>
          </div>
          <div className="kpi-card">
            <p className="text-sm font-medium text-slate-500">Rejected</p>
            <p className="mt-1 text-2xl font-bold text-red-600">{stats.rejected}</p>
          </div>
        </div>

        {/* Recent Timesheets */}
        <div className="card-base">
          <div className="card-header flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Recent Timesheets</h2>
            <Link to="/timesheets" className="text-sm text-[#194eb2] hover:underline flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3">Period</th>
                  <th className="px-6 py-3">Days</th>
                  <th className="px-6 py-3">Hours</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {myTimesheets.slice(0, 5).map((ts) => (
                  <tr key={ts.id} className="table-row">
                    <td className="px-6 py-4 text-sm text-slate-900">
                      {formatDateRange(ts.period_start, ts.period_end)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{ts.total_days > 0 ? ts.total_days : '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{ts.total_hours > 0 ? ts.total_hours : '-'}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={ts.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/timesheets/${ts.id}`}
                        className="text-sm text-[#194eb2] hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const DashboardPage = () => {
  const { isAdmin } = useAuth();
  return isAdmin ? <AdminDashboard /> : <SubcontractorDashboard />;
};

export default DashboardPage;
